import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppSpin } from '@/components/atoms';
import PatientAddressFormBottomBar from '../components/PatientAddressFormBottomBar';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { useMainPatient } from '@/hooks/main/useMainPatient';
import { usePatientAddressCreateMutation } from '@/hooks/main/usePatientAddresses';
import PatientAddressForm from '../components/PatientAddressForm';
import type { PatientAddressFormData } from '@/schemas/patientAddressSchema';
import { NotFound } from '@/pages/main/system/NotFound';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';

const CreatePatientAddressPage: React.FC = () => {
  const { id, tenant } = useParams<{ id: string; tenant?: string }>();
  const navigate = useNavigate();
  const patientId = id ? parseInt(id, 10) : null;

  const { data: patient, isLoading: patientLoading } = useMainPatient(patientId ?? undefined);
  const { createMutation, isLoading: isCreating } = usePatientAddressCreateMutation();

  const handleBack = useCallback(() => {
    navigate(`/${tenant}/patient_addresses/${id}/list`);
  }, [navigate, tenant, id]);

  const handleSubmit = useCallback(
    (data: PatientAddressFormData) => {
      if (!patientId) return;
      return createMutation({ patientId, data }).then(() => {
        authorizeNavigation();
        navigate(`/${tenant}/patient_addresses/${id}/list`);
      });
    },
    [patientId, createMutation, navigate, tenant, id]
  );

  if (patientId === null || isNaN(patientId)) {
    return <NotFound />;
  }

  if (patientLoading && !patient) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <AppSpin size="large" />
      </div>
    );
  }

  if (!patient) {
    return <NotFound />;
  }

  const formId = 'patient-address-create-form';

  return (
    <div>
      <PatientPageHeader
        patient={patient}
        patientId={id!}
        tenant={tenant}
        navigate={navigate}
        activeKey="patient-addresses"
        disabled={isCreating}
      />

      <PatientAddressForm formId={formId} onSubmit={handleSubmit} />

      <PatientAddressFormBottomBar
        mode="create"
        isSubmitting={isCreating}
        formId={formId}
        onBack={handleBack}
      />
    </div>
  );
};

export default CreatePatientAddressPage;
